// 致命を弾いた時の押し戻しの「重さ」を、カメラの寄り・フラッシュ・揺れ・止めで出す (2026-10-09 夜に比較版として作った)。
//   既定 = c (10-10 ユーザー答え push-weight・致命の弾きと一緒に配布版へ・execPush=new の時に重ねて効く)。
//   DEV の ?execPushWeight=off|a|b で見比べる (off = 重ねない new だけ)。URL を読むのは game.ts の DEV の箱の中だけ。
//
// ユーザー (10-09・見比べページの memo): 「新しい版で割といい感じで、あとはエフェクトと言うよりカメラの寄りとか
//   フラッシュとか揺れとかで重さを表現してほしい。ゼンゼロのパリィは敵の強さや攻撃の重さによって音や
//   プレイアブルキャラクターの構えや身のこなしが変わっているので大いに参考になると思う」
// 手本の観察と値の根拠 = docs/research/2026-10-09-exec-push-weight-camera-status.md。
//
//   a = 寄って止める (カメラの寄りが主役・全ボス同じ): 接点へ 10% 寄る・止め 110ms・接点だけ光る・押される向きへ一突き。
//   b = 白く飛ばして沈む (フラッシュが主役・全ボス同じ): 画面全体が白く飛ぶ→一瞬暗く沈む・止め 90ms・縦にドンと落ちる揺れ。
//   c = ボスの重さで段 (ゼンゼロの「重い相手ほど止めが長い」): 花嫁 < 騎士 < 戴冠獣 < 介錯 < 覚醒王 で、止め・寄り・揺れ・光を強める。
//       軽いボスは接点だけ光り、戴冠獣から上は画面ごと金に染まる。
//
// 時計: 弾いた瞬間を 0ms とする。止め (hitStop) の間はゲームの時刻が進まないので、止めの残り (state.hitStopRemaining)
//   から止めの中の経過を出し、止め明けは「止めの長さ＋止め明けからの経過」で数える (pushWeightClockMs)。
//   寄りは止めの間に寄り切って止め明けから戻る・揺れは止め明けから始まる (止めの間は画面が静止して見える)。
//   フラッシュは弾いた瞬間に最大・止めの間は保つ・止め明けから消える。
// 線・円・輪・放射状の線は描かない (ユーザーの決まり)。光は輪郭の無いにじんだ面と、画面全体の色だけ。
// 数値はここに集めてある。差し替える時はここだけ直す。

export type PushWeightVariant = 'off' | 'a' | 'b' | 'c'
/** 既定 = c ボスの重さで段 (10-10 答えるページ push-weight「c」・致命の弾きを配布版へ入れた時に一緒に既定へ)。 */
export const PUSH_WEIGHT_DEFAULT: PushWeightVariant = 'c'
let variant: PushWeightVariant = PUSH_WEIGHT_DEFAULT

/** URL の値から切り替える。'off' / 'a' / 'b' / 'c' はその版、それ以外 (指定なし・知らない値) は既定 (c)。 */
export function setPushWeight(value: string | null | undefined): PushWeightVariant {
  variant = value === 'off' || value === 'a' || value === 'b' || value === 'c' ? value : PUSH_WEIGHT_DEFAULT
  return variant
}
export function pushWeightVariant(): PushWeightVariant { return variant }

export type PushWeightSpec = {
  /** 弾いた瞬間の止め (ms)。押し戻しの滑りは止め明けから。 */
  hitStopMs: number
  /** カメラの寄り。pivot = どこへ寄るか (contact = 刀の接点 / player = 主人公の胸 / between = 主人公とボスの真ん中・接点の高さ)。
   *  amount = 何割大きく見せるか (0.10 = 10%)。inMs で寄り切り (止めの中)・止め明けから outMs で戻る。 */
  zoom: { pivot: 'contact' | 'player' | 'between'; amount: number; inMs: number; outMs: number }
  /** フラッシュ。弾いた瞬間に最大・止めの間は保つ・止め明けから fadeMs で消える。
   *  contact = 接点だけのにじんだ光の面 (peak = 不透明度・radiusPx = 消えきる半径)。
   *  screen = 画面全体 (white = 白く飛ぶ / tint = 金に染まる)。dark = 白の後に一瞬暗く沈む (止め明けから startMs で始まり ms で戻る)。 */
  flash: {
    fadeMs: number
    contact?: { peak: number; radiusPx: number }
    screen?: { mode: 'white' | 'tint'; peak: number }
    dark?: { peak: number; startMs: number; ms: number }
  }
  /** 揺れ (止め明けから)。push = 押される向きへ一突きして小さく返る / down = 縦にドンと落ちて2回弾む。
   *  amplitudePx = 最初の振れ幅・decayMs で消える・crossShare = もう一方の軸へ混ぜる割合。 */
  shake: { dir: 'push' | 'down'; amplitudePx: number; decayMs: number; crossShare: number }
}

export const PUSH_WEIGHT: Record<'a' | 'b', PushWeightSpec> = {
  a: {
    hitStopMs: 110,
    zoom: { pivot: 'contact', amount: 0.10, inMs: 50, outMs: 400 },
    flash: { fadeMs: 160, contact: { peak: 0.6, radiusPx: 160 } },
    shake: { dir: 'push', amplitudePx: 7, decayMs: 240, crossShare: 0.2 },
  },
  b: {
    hitStopMs: 90,
    zoom: { pivot: 'player', amount: 0.05, inMs: 0, outMs: 260 },
    flash: { fadeMs: 110, screen: { mode: 'white', peak: 0.55 }, dark: { peak: 0.3, startMs: 60, ms: 220 } },
    shake: { dir: 'down', amplitudePx: 7, decayMs: 260, crossShare: 0.3 },
  },
}

/** c = ボスの重さで段。軽い順に 花嫁 < 騎士 < 戴冠獣 < 介錯 < 覚醒王 (押される距離 130〜160px と同じ並び)。 */
export const PUSH_WEIGHT_C_BY_BOSS: Record<string, PushWeightSpec> = {
  bride: {
    hitStopMs: 60,
    zoom: { pivot: 'between', amount: 0.04, inMs: 40, outMs: 220 },
    flash: { fadeMs: 120, contact: { peak: 0.35, radiusPx: 120 } },
    shake: { dir: 'push', amplitudePx: 3, decayMs: 150, crossShare: 0.2 },
  },
  'vigil-knight': {
    hitStopMs: 100,
    zoom: { pivot: 'between', amount: 0.08, inMs: 50, outMs: 320 },
    flash: { fadeMs: 160, contact: { peak: 0.5, radiusPx: 150 } },
    shake: { dir: 'push', amplitudePx: 6, decayMs: 230, crossShare: 0.25 },
  },
  'dream-hound': {
    hitStopMs: 115,
    zoom: { pivot: 'between', amount: 0.09, inMs: 60, outMs: 360 },
    flash: { fadeMs: 180, contact: { peak: 0.5, radiusPx: 150 }, screen: { mode: 'tint', peak: 0.2 } },
    shake: { dir: 'push', amplitudePx: 7, decayMs: 260, crossShare: 0.3 },
  },
  'the-executioner': {
    hitStopMs: 140,
    zoom: { pivot: 'between', amount: 0.11, inMs: 70, outMs: 420 },
    flash: { fadeMs: 200, contact: { peak: 0.55, radiusPx: 160 }, screen: { mode: 'tint', peak: 0.38 } },
    shake: { dir: 'push', amplitudePx: 9, decayMs: 300, crossShare: 0.35 },
  },
  'crowned-king-awoken': {
    hitStopMs: 170,
    zoom: { pivot: 'between', amount: 0.14, inMs: 80, outMs: 480 },
    flash: { fadeMs: 220, contact: { peak: 0.6, radiusPx: 170 }, screen: { mode: 'tint', peak: 0.55 } },
    shake: { dir: 'push', amplitudePx: 11, decayMs: 340, crossShare: 0.4 },
  },
}
/** c で表に無いボス (中くらい = 騎士と同じ)。 */
export const PUSH_WEIGHT_C_DEFAULT: PushWeightSpec = PUSH_WEIGHT_C_BY_BOSS['vigil-knight']

/** この版・このボスの決まり。'off' は null (今のまま)。 */
export function pushWeightSpec(v: PushWeightVariant, bossId: string): PushWeightSpec | null {
  if (v === 'off') return null
  if (v === 'c') return PUSH_WEIGHT_C_BY_BOSS[bossId] ?? PUSH_WEIGHT_C_DEFAULT
  return PUSH_WEIGHT[v]
}

/** 弾いた瞬間からの経過 (ms・止めの中も数える)。ageSec = ゲームの時刻での経過 (止めの間は 0 のまま)。 */
export function pushWeightClockMs(ageSec: number, hitStopRemainingSec: number, spec: PushWeightSpec): number {
  if (ageSec <= 1e-6 && hitStopRemainingSec > 0) return Math.max(0, spec.hitStopMs - hitStopRemainingSec * 1000)
  return spec.hitStopMs + Math.max(0, ageSec) * 1000
}

const easeOut = (k: number): number => 1 - (1 - k) * (1 - k)
const easeInOut = (k: number): number => k * k * (3 - 2 * k)

/** 寄りの強さ (0..1)。inMs で寄り切り・止め明け (または寄り切り) から outMs で戻る。 */
export function pushWeightZoomLevel(clockMs: number, spec: PushWeightSpec): number {
  const z = spec.zoom
  if (clockMs < 0 || z.amount <= 0) return 0
  if (z.inMs > 0 && clockMs < z.inMs) return easeOut(clockMs / z.inMs)
  const outStart = Math.max(spec.hitStopMs, z.inMs)
  if (clockMs <= outStart) return 1
  const k = (clockMs - outStart) / z.outMs
  return k >= 1 ? 0 : 1 - easeInOut(k)
}
/** 画面の倍率 (1 = 寄らない)。 */
export function pushWeightZoom(clockMs: number, spec: PushWeightSpec): number {
  return 1 + spec.zoom.amount * pushWeightZoomLevel(clockMs, spec)
}

/** フラッシュの明るさ (0..1)。弾いた瞬間に 1・止めの間は 1・止め明けから fadeMs で 0。 */
export function pushWeightFlashLevel(clockMs: number, spec: PushWeightSpec): number {
  if (clockMs < 0) return 0
  const t = clockMs - spec.hitStopMs
  if (t <= 0) return 1
  const k = t / spec.flash.fadeMs
  return k >= 1 ? 0 : (1 - k) * (1 - k)
}
/** 白の後に一瞬暗く沈む強さ (0..1・b だけ)。 */
export function pushWeightDarkLevel(clockMs: number, spec: PushWeightSpec): number {
  const d = spec.flash.dark
  if (!d) return 0
  const k = (clockMs - spec.hitStopMs - d.startMs) / d.ms
  if (k <= 0 || k >= 1) return 0
  // 最初の 3 割で沈み切り、残りで滑らかに戻る。
  return k < 0.3 ? Math.sin((Math.PI / 2) * (k / 0.3)) : easeInOut((1 - k) / 0.7)
}

/** 揺れ (止め明けから・{x, y} ゲーム px)。dir = 押される向き (+1 = 右へ)。 */
export function pushWeightShake(clockMs: number, spec: PushWeightSpec, dir: number): { x: number; y: number } {
  const s = spec.shake
  const t = clockMs - spec.hitStopMs
  if (t < 0 || s.amplitudePx <= 0 || t >= s.decayMs) return { x: 0, y: 0 }
  const u = t / s.decayMs
  const env = (1 - u) * (1 - u)
  if (s.dir === 'push') {
    // 押される向きへ一突き (止め明けの最初のコマで最大)・u≈0.31 で戻り・小さく返って止まる。
    const main = s.amplitudePx * env * Math.cos(1.6 * Math.PI * u)
    return { x: dir * main, y: s.crossShare * s.amplitudePx * env }
  }
  // 縦にドンと落ちて (下が +) 2回弾む。横は押される向きへ少しだけ流れる。
  return { x: dir * s.crossShare * s.amplitudePx * env, y: s.amplitudePx * env * Math.cos(4 * Math.PI * u) }
}

/** 寄りの中心 (ゲーム座標)。contactX/Y = 弾いた接点・playerX/Y = 主人公の足元・bossX = ボスの x (どれも弾いた瞬間)。 */
export function pushWeightPivot(spec: PushWeightSpec, contactX: number, contactY: number, playerX: number, playerY: number, bossX: number): { x: number; y: number } {
  if (spec.zoom.pivot === 'player') return { x: playerX, y: playerY - 70 }
  if (spec.zoom.pivot === 'between') return { x: (playerX + bossX) / 2, y: contactY }
  return { x: contactX, y: contactY }
}

/** 弾いた瞬間から、寄り・光・揺れが全部収まるまで (ms)。光の長い残り (0.7 秒) の寿命の中に収まっていること。 */
export function pushWeightTotalMs(spec: PushWeightSpec): number {
  const d = spec.flash.dark
  return spec.hitStopMs + Math.max(spec.zoom.outMs + Math.max(0, spec.zoom.inMs - spec.hitStopMs), spec.flash.fadeMs,
    d ? d.startMs + d.ms : 0, spec.shake.decayMs)
}

// ---------------- 描く (render.ts から) ----------------

const WARM = '255,236,190'
const COLD = '228,232,248'

/** 接点だけのにじんだ光の面 (加算・輪郭なし)。sx, sy = 画面座標。 */
export function drawPushWeightContactFlash(ctx: CanvasRenderingContext2D, sx: number, sy: number, level: number, spec: PushWeightSpec, isJust: boolean): void {
  const c = spec.flash.contact
  if (!c || level <= 0.003) return
  const col = isJust ? WARM : COLD
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, c.radiusPx)
  g.addColorStop(0, `rgba(${col},${(c.peak * level).toFixed(3)})`)
  g.addColorStop(0.4, `rgba(${col},${(c.peak * level * 0.35).toFixed(3)})`)
  g.addColorStop(1, `rgba(${col},0)`)
  ctx.fillStyle = g
  ctx.fillRect(sx - c.radiusPx, sy - c.radiusPx, c.radiusPx * 2, c.radiusPx * 2)
  ctx.restore()
}

/** 画面全体のフラッシュ (白く飛ぶ／金に染まる) と、その後の暗い沈み。w, h = 画面の大きさ。sx, sy = 接点の画面座標。 */
export function drawPushWeightScreenFlash(ctx: CanvasRenderingContext2D, w: number, h: number, sx: number, sy: number,
  level: number, dark: number, spec: PushWeightSpec, isJust: boolean): void {
  const s = spec.flash.screen
  ctx.save()
  if (s && level > 0.003) {
    if (s.mode === 'white') {
      ctx.globalCompositeOperation = 'source-over'
      ctx.fillStyle = `rgba(${isJust ? '255,250,238' : '244,246,255'},${(s.peak * level).toFixed(3)})`
      ctx.fillRect(0, 0, w, h)
    } else {
      // 金に染まる (崩した瞬間 drawParryDownTint と同じ作り・強さは peak で)。
      const a = s.peak * level
      ctx.globalCompositeOperation = 'soft-light'
      ctx.fillStyle = isJust ? `rgba(255,190,70,${(0.95 * a).toFixed(3)})` : `rgba(200,205,240,${(0.95 * a).toFixed(3)})`
      ctx.fillRect(0, 0, w, h)
      ctx.globalCompositeOperation = 'lighter'
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, w * 0.7)
      g.addColorStop(0, isJust ? `rgba(255,200,100,${(0.55 * a).toFixed(3)})` : `rgba(220,225,255,${(0.5 * a).toFixed(3)})`)
      g.addColorStop(1, isJust ? `rgba(255,170,60,${(0.16 * a).toFixed(3)})` : `rgba(180,185,230,${(0.14 * a).toFixed(3)})`)
      ctx.fillStyle = g
      ctx.fillRect(0, 0, w, h)
    }
  }
  const d = spec.flash.dark
  if (d && dark > 0.003) {
    ctx.globalCompositeOperation = 'source-over'
    ctx.fillStyle = `rgba(8,6,16,${(d.peak * dark).toFixed(3)})`
    ctx.fillRect(0, 0, w, h)
  }
  ctx.restore()
}
